import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { TokenRefreshStrategy } from './index';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
    private refreshTokenSubject = new Subject<string | undefined>();
    private isRefreshing = false;

    constructor(
        @Inject('REFRESH_STRATEGIES')
        private readonly strategies: TokenRefreshStrategy[]
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Block requests from executing while a token is resolving (except for request important to login)
        if (this.isRefreshing && !req.url.match(/token|login|portal-api/)) {
            return this.waitForRefreshAndContinue(req, next);
        }

        return next.handle(req).pipe(
            catchError(error => {
                // Handle 401 Unauthorized responses
                if (error.status === 401 && !this.isRefreshing) {
                    return this.handle401Error(req, next);
                }
                // During parallel calls refresh might be only triggered while already here
                if (error.status === 401 && this.isRefreshing) {
                    return this.waitForRefreshAndContinue(req, next);
                }

                return throwError(() => error);
            })
        );
    }

    private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            return this.tryRefreshChain(req, next, 0);
        }
        return this.waitForRefreshAndContinue(req, next);
    }

    private tryRefreshChain(req: HttpRequest<any>, next: HttpHandler, index: number): Observable<HttpEvent<any>> {
        if (index >= this.strategies.length) {
            return throwError(() => new Error('All refresh strategies failed'));
        }
        const strategy: TokenRefreshStrategy = this.strategies[index];

        const tokenRefreshStream$: Observable<HttpEvent<any>> = strategy
            .refreshToken(req, next, this.refreshTokenSubject)
            .pipe(
                catchError(() => {
                    return this.tryRefreshChain(req, next, index + 1);
                }),
                tap(() => {
                    this.isRefreshing = false;
                }),
                shareReplay(1)
            );

        // Make sure the stream resolves at least once so other requests aren't stuck waiting for the token refresh
        tokenRefreshStream$.pipe(take(1)).subscribe();

        return tokenRefreshStream$;
    }

    private waitForRefreshAndContinue(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.refreshTokenSubject.pipe(
            filter((refreshToken: string | undefined) => refreshToken !== undefined),
            take(1),
            switchMap(() => next.handle(req))
        );
    }
}
